import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import { BanknoteIcon, MessageSquareIcon, CheckIcon, XIcon, DollarSignIcon, ReceiptIcon } from 'lucide-react';

// Make sure you have these packages installed:
// npm install react-bootstrap bootstrap lucide-react

function AddManualEntryModal({ show, handleClose }) {
  const [formData, setFormData] = useState({
    rent: "",
    deposit: "",
    tolls: "",
    damage: "",
    others: "",
    remarks: ""
  });
  const [total, setTotal] = useState(0);

  // Calculate total whenever form data changes
  useEffect(() => {
    const sum = Object.keys(formData)
      .filter(key => key !== "remarks")
      .reduce((sum, key) => sum + (parseFloat(formData[key]) || 0), 0);
    
    setTotal(sum);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = () => {
    // Handle the payment settlement logic here
    //console.log("Payment settled:", formData, "Total:", total);
    handleClose();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      backdrop="static" 
      keyboard={false}
      centered
    >
      <Modal.Header className="text-white" style={{backgroundColor:'var(--primary-color)'}}>
        <Modal.Title className="d-flex align-items-center">
          <BanknoteIcon className="me-2" size={24} />
          Add Manual Entry
        </Modal.Title>
        <Button 
          variant="link" 
          className="close-button text-white" 
          onClick={handleClose}
        >
          <XIcon size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="px-4 py-4">
        <div className="payment-section mb-4">
          <h5 className="section-title mb-3 d-flex align-items-center">
            <ReceiptIcon className="me-2" size={20} />
            Payment Details
          </h5>
          
          <Row className="mb-4 g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Rent</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}} >
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="rent"
                    value={formData.rent}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="form-control-lg"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Deposit</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}}>
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="deposit"
                    value={formData.deposit}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="form-control-lg"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Tolls</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}}>
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="tolls"
                    value={formData.tolls}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="form-control-lg"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-4 g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Damage</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}}>
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="damage"
                    value={formData.damage}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="form-control-lg"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Others</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}}>
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="others"
                    value={formData.others}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="form-control-lg"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Total Amount</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light" style={{marginTop:'5px'}}>
                    <DollarSignIcon size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={total.toFixed(2)}
                    readOnly
                    className="form-control-lg bg-light fw-bold text-primary"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </div>
        
        <div className="remarks-section">
          <h5 className="section-title mb-3 d-flex align-items-center">
            <MessageSquareIcon className="me-2" size={20} />
            Additional Information
          </h5>
          
          <Form.Group>
            <Form.Label className="fw-bold">Remarks</Form.Label>
            <Form.Control
              as="textarea"
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows={3}
              placeholder="Add any additional notes or comments here..."
              className="remarks-textarea"
            />
          </Form.Group>
        </div>
      </Modal.Body>
      
      <Modal.Footer className="bg-light">
        <Button 
          variant="outline-secondary" 
          onClick={handleClose}
          className="px-4 py-2 d-flex align-items-center"
        >
          <XIcon className="me-2" size={18} />
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          className="px-4 py-2 ms-2 d-flex align-items-center"
        >
          <CheckIcon className="me-2" size={18} />
          Add Payment
        </Button>
      </Modal.Footer>
      
      <style jsx>{`
        .section-title {
          color: #495057;
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 8px;
        }
        
        .payment-section, .remarks-section {
          background-color: #ffffff;
          border-radius: 8px;
        }
        
        .form-control-lg {
          height: 50px;
          font-size: 16px;
        }
        
        .form-control:focus {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
        
        .remarks-textarea {
          min-height: 100px;
          resize: vertical;
        }
        
        .close-button {
          box-shadow: none !important;
          padding: 0;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modal-content {
          border-radius: 10px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          border: none;
        }
        
        /* Override Bootstrap styles */
        .form-label {
          margin-bottom: 0.5rem;
        }
        
        .input-group-text {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
        }
      `}</style>
    </Modal>
  );
}

export default AddManualEntryModal;